import React, { useState, useEffect } from 'react';

// logo.jpg is a flat JPEG with a solid white square behind the mark — a JPEG can't carry
// real transparency, and CSS blend-mode tricks (mix-blend-multiply) only "erase" that white
// reliably when the element's stacking context lines up just right, which left a faint
// white box showing through on some backgrounds (the gold hero/footer, the new bead-pattern
// watermark, etc). Instead we color-key the white out for real, once per session: draw the
// JPEG onto an offscreen canvas, zero out the alpha of every near-white pixel, and cache the
// resulting transparent PNG data URL so every <Logo/> instance reuses it instantly.
let transparentLogoCache = null;
let transparentLogoPromise = null;

const makeLogoTransparent = (src) => {
  if (transparentLogoCache) return Promise.resolve(transparentLogoCache);
  if (transparentLogoPromise) return transparentLogoPromise;

  transparentLogoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
          if (brightness > 245) {
            data[i + 3] = 0; // fully background — drop it
          } else if (brightness > 220) {
            // Anti-aliased edge pixels between the mark and the background — fade them
            // out proportionally instead of a hard cutoff, so edges stay smooth.
            data[i + 3] = Math.round(255 * (245 - brightness) / 25);
          }
        }
        ctx.putImageData(imageData, 0, 0);
        transparentLogoCache = canvas.toDataURL('image/png');
        resolve(transparentLogoCache);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = src;
  });
  return transparentLogoPromise;
};

/**
 * Renders /logo.jpg (background removed at runtime, see above) if present in the public
 * folder, falling back to a CSS/SVG approximation of the brand mark (black ring + "P" +
 * gold accent) so the site still looks on-brand before the real asset is added.
 */
const Logo = ({ className = 'h-11 w-11' }) => {
  const [imgError, setImgError] = useState(false);
  const [src, setSrc] = useState(transparentLogoCache || '/logo.jpg');

  useEffect(() => {
    let cancelled = false;
    makeLogoTransparent('/logo.jpg')
      .then((dataUrl) => {
        if (!cancelled) setSrc(dataUrl);
      })
      .catch(() => {
        // Canvas processing failed (e.g. blocked by a strict CSP) — the plain JPEG
        // (already set as the initial src) still renders, just with its white square.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return !imgError ? (
    <img
      src={src}
      alt="Prestige"
      className={`${className} object-contain`}
      onError={() => setImgError(true)}
    />
  ) : (
    <svg viewBox="0 0 100 100" className={className} role="img" aria-label="Prestige">
      <circle cx="50" cy="50" r="46" fill="none" stroke="#171412" strokeWidth="3" />
      <path
        d="M40 26 h14 a14 14 0 0 1 0 28 h-8 v20 h-6 z"
        fill="#171412"
      />
      <path
        d="M56 40 a12 12 0 1 0 4 20"
        fill="none"
        stroke="#C1892F"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle cx="54" cy="66" r="3.2" fill="#171412" />
    </svg>
  );
};

export default Logo;
